import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Save, Loader2, Calendar, MapPin, Image as ImageIcon,
  Clock, BarChart3, ShoppingCart, Music, Ticket, UserPlus, Users,
  QrCode, Settings, Eye, Trash2, Upload, Plus, Edit2, AlertTriangle,
  Lock, XCircle, CheckCircle, Send, Edit, Info, GripVertical,
  Mail, DollarSign, RefreshCw, MoreHorizontal, Copy, ExternalLink, Percent, Youtube, Tag,
  Activity, Megaphone, FileText, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { eventsApi } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import CloudinaryUploader from '../../components/CloudinaryUploader';
import VenueMapViewer from '../../components/venue/VenueMapViewer';
import VenueStatsTab from '../../components/producer/VenueStatsTab';
import HeatmapTab from '../../components/producer/HeatmapTab';
import CoProductionTab from '../../components/producer/CoProductionTab';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const TABS = [
  { id: 'info', label: 'Información', icon: Settings },
  { id: 'tickets', label: 'Entradas', icon: Ticket },
  { id: 'venue', label: 'Venue', icon: MapPin },
  { id: 'reports', label: 'Reportes', icon: BarChart3 },
  { id: 'orders', label: 'Órdenes', icon: ShoppingCart },
  { id: 'lineup', label: 'Lineup', icon: Music },
  { id: 'invitations', label: 'Invitaciones', icon: UserPlus },
  { id: 'rrpp', label: 'RRPP', icon: Users },
  { id: 'heatmap', label: 'Heatmap', icon: Activity },
  { id: 'co_production', label: 'Co-producción', icon: Users },
];

// Sortable Artist Item Component - defined outside to prevent re-renders
const SortableArtistItem = ({ artist, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: artist.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 flex items-center gap-4"
    >
      <div {...attributes} {...listeners} className="cursor-grab hover:text-[#D4AF37]">
        <GripVertical size={20} />
      </div>
      <img 
        src={artist.image_url || 'https://via.placeholder.com/60'} 
        alt={artist.name}
        className="w-14 h-14 rounded-lg object-cover"
      />
      <div className="flex-1 min-w-0">
        <p className="font-bold text-white truncate">{artist.name}</p>
        <p className="text-sm text-zinc-400 truncate">{artist.schedule} - {artist.stage}</p>
      </div>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(artist)}
          className="hover:text-[#D4AF37]"
        >
          <Edit2 size={16} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(artist.id)}
          className="text-red-400 hover:text-red-300"
        >
          <Trash2 size={16} />
        </Button>
      </div>
    </div>
  );
};

const AdminEventManagePage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    venue_name: '',
    google_maps_url: '',
    image_url: '',
    youtube_video_url: '',  // YouTube video for event banner
    ticket_price: 0,
    ticket_quantity: 100,
    service_fee: 10,
    service_fee_type: 'percentage',
    queue_enabled: false,
    queue_max_concurrent: 50,
  });

  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({
    name: '',
    price: 0,
    quantity: 100,
    description: '',
    is_active: true,
    free_service_for_subscribers: false,
    tier_type: 'sale',
    inquiry_label: '',
    is_sold_out: false,
    max_per_order: 10,
    price_stages: [],
  });

  // Artists/Lineup state
  const [artists, setArtists] = useState([]);
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [newArtist, setNewArtist] = useState({
    name: '',
    image_url: '',
    description: '',
    schedule: '',
    stage: '',
  });

  // DnD sensors for lineup
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [editOrderForm, setEditOrderForm] = useState({ buyer_name: '', buyer_email: '', buyer_phone: '', buyer_dni: '', status: '', admin_notes: '' });
  const [refundForm, setRefundForm] = useState({ partial_amount: '', reason: '' });
  const [actionLoading, setActionLoading] = useState(false);
  // NEW: Individual tickets state for order details
  const [orderTickets, setOrderTickets] = useState([]);
  const [orderTicketsLoading, setOrderTicketsLoading] = useState(false);
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [editingTicketDni, setEditingTicketDni] = useState('');

  // Reports state
  const [report, setReport] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);

  // RRPP state
  const [rrpps, setRRPPs] = useState([]);
  const [rrppLoading, setRRPPLoading] = useState(false);
  const [showRRPPModal, setShowRRPPModal] = useState(false);
  const [newRRPP, setNewRRPP] = useState({ name: '', email: '', phone: '', commission_percent: 10 });

  // Invitations state
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ emails: '', tier_id: '', quantity_per_invite: 1 });

  // Venue state
  const [venue, setVenue] = useState(null);
  const [venueLoading, setVenueLoading] = useState(false);

  useEffect(() => {
    fetchEventData();
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/events/${eventId}`);
      const eventData = response.data;
      setEvent(eventData);
      setFormData({
        title: eventData.title || '',
        description: eventData.description || '',
        date: eventData.date ? eventData.date.split('T')[0] : '',
        time: eventData.time || '',
        location: eventData.location || '',
        venue_name: eventData.venue_name || '',
        google_maps_url: eventData.google_maps_url || '',
        image_url: eventData.image_url || '',
        youtube_video_url: eventData.youtube_video_url || '',
        ticket_price: eventData.ticket_price || 0,
        ticket_quantity: eventData.ticket_quantity || 100,
        service_fee: eventData.service_fee || 10,
        service_fee_type: eventData.service_fee_type || 'percentage',
        queue_enabled: eventData.queue_enabled || false,
        queue_max_concurrent: eventData.queue_max_concurrent || 50,
        custom_terms: eventData.custom_terms || '',
        custom_terms_locked: !!eventData.custom_terms_locked,
        include_terms_in_email: !!eventData.include_terms_in_email,
      });
      setArtists(eventData.artists || []);
      
      // Fetch tickets
      try {
        const ticketsRes = await api.get(`/events/${eventId}/tiers`);
        setTickets(ticketsRes.data || []);
      } catch (e) {
        console.log('No tickets found');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Error al cargar el evento');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Clean up formData - remove empty strings for datetime fields
      const cleanedData = { ...formData };
      
      // Don't send empty date strings
      if (!cleanedData.date || cleanedData.date === '') {
        delete cleanedData.date;
      }
      if (!cleanedData.end_date || cleanedData.end_date === '') {
        delete cleanedData.end_date;
      }
      // Don't send empty image_url
      if (!cleanedData.image_url || cleanedData.image_url === '') {
        delete cleanedData.image_url;
      }
      // Handle youtube_video_url - convert empty string to null, keep valid URLs
      if (cleanedData.youtube_video_url === '') {
        cleanedData.youtube_video_url = null;
      }
      // Custom terms: empty → null so it's cleared cleanly
      if (cleanedData.custom_terms === '' || cleanedData.custom_terms == null) {
        cleanedData.custom_terms = null;
        // No terms ⇒ nothing to lock or include in email
        cleanedData.custom_terms_locked = false;
        cleanedData.include_terms_in_email = false;
      }
      
      await api.put(`/events/${eventId}`, {
        ...cleanedData,
        artists: artists,
      });
      toast.success('Evento guardado correctamente');
      fetchEventData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors
        const errorMsg = detail.map(e => e.msg || e.message).join(', ');
        toast.error(errorMsg || 'Error de validación');
      } else {
        toast.error('Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEvent = async () => {
    if (cancelConfirmText !== 'CANCELAR EVENTO') {
      toast.error('Escribe "CANCELAR EVENTO" para confirmar');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/events/${eventId}/cancel`);
      toast.success('Evento cancelado y bloqueado');
      setShowCancelModal(false);
      setCancelConfirmText('');
      fetchEventData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    } finally {
      setSaving(false);
    }
  };

  // Ticket functions
  const handleSaveTicket = async () => {
    const px = Number(newTicket.promo_pay_x || 0);
    const gy = Number(newTicket.promo_get_y || 0);
    if ((px > 0 || gy > 0) && gy <= px) {
      toast.error('En la promo, "Llevás" debe ser mayor que "Pagás" (ej: pagás 1, llevás 2)');
      return;
    }
    const payload = {
      ...newTicket,
      promo_pay_x: px,
      promo_get_y: gy,
      promo_valid_until: newTicket.promo_valid_until ? new Date(newTicket.promo_valid_until).toISOString() : null,
      promo_max_redemptions: Number(newTicket.promo_max_redemptions || 0),
    };
    try {
      if (editingTicket) {
        await api.put(`/events/${eventId}/tiers/${editingTicket.id}`, payload);
        toast.success('Entrada actualizada');
      } else {
        await api.post(`/events/${eventId}/tiers`, payload);
        toast.success('Entrada creada');
      }
      setShowTicketModal(false);
      setEditingTicket(null);
      setNewTicket({ name: '', price: 0, quantity: 100, description: '', is_active: true, free_service_for_subscribers: false });
      // Refresh tickets
      const ticketsRes = await api.get(`/events/${eventId}/tiers`);
      setTickets(ticketsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar entrada');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!confirm('¿Eliminar esta entrada?')) return;
    try {
      await api.delete(`/events/${eventId}/tiers/${ticketId}`);
      toast.success('Entrada eliminada');
      const ticketsRes = await api.get(`/events/${eventId}/tiers`);
      setTickets(ticketsRes.data || []);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Artist functions
  const handleSaveArtist = async () => {
    if (!newArtist.name) {
      toast.error('El nombre del artista es requerido');
      return;
    }

    try {
      if (editingArtist) {
        await api.put(`/events/${eventId}/lineup/${editingArtist.id}`, newArtist);
        toast.success('Artista actualizado');
      } else {
        await api.post(`/events/${eventId}/lineup`, newArtist);
        toast.success('Artista agregado');
      }
      setShowArtistModal(false);
      setEditingArtist(null);
      setNewArtist({ name: '', image_url: '', description: '', schedule: '', stage: '' });
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar artista');
    }
  };

  const handleDeleteArtist = async (artistId) => {
    if (!confirm('¿Eliminar este artista del lineup?')) return;
    try {
      await api.delete(`/events/${eventId}/lineup/${artistId}`);
      toast.success('Artista eliminado');
      fetchEventData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = artists.findIndex((a) => a.id === active.id);
      const newIndex = artists.findIndex((a) => a.id === over.id);
      const newOrder = arrayMove(artists, oldIndex, newIndex);
      setArtists(newOrder);
      
      try {
        await api.put(`/events/${eventId}/lineup/order`, {
          artist_ids: newOrder.map(a => a.id)
        });
      } catch (error) {
        console.error('Error updating order:', error);
      }
    }
  };

  // ========== ORDERS FUNCTIONS ==========
  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const res = await eventsApi.getOrders(eventId);
      setOrders(res.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error al cargar órdenes');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setEditOrderForm({
      buyer_name: order.buyer_name || '',
      buyer_email: order.buyer_email || '',
      buyer_phone: order.buyer_phone || '',
      buyer_dni: order.buyer_dni || '',
      status: order.status || '',
      admin_notes: order.admin_notes || ''
    });
    setOrderTickets([]); // Reset tickets
    setEditingTicketId(null);
    setShowEditOrderModal(true);
    // Load tickets for this order
    fetchOrderTickets(order.id);
  };

  const handleSaveEditOrder = async () => {
    setActionLoading(true);
    try {
      await eventsApi.updateOrder(eventId, selectedOrder.id, editOrderForm);
      toast.success('Orden actualizada');
      setShowEditOrderModal(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendTickets = async (order) => {
    setActionLoading(true);
    try {
      await eventsApi.resendOrderTickets(eventId, order.id);
      toast.success(`Tickets reenviados a ${order.buyer_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reenviar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenRefund = (order) => {
    setSelectedOrder(order);
    setRefundForm({ partial_amount: '', reason: '' });
    setShowRefundModal(true);
  };

  const handleRefund = async (isPartial = false) => {
    setActionLoading(true);
    try {
      const partialAmount = isPartial && refundForm.partial_amount ? parseFloat(refundForm.partial_amount) : null;
      await eventsApi.refundOrder(eventId, selectedOrder.id, partialAmount, refundForm.reason);
      toast.success(isPartial ? 'Reembolso parcial registrado' : 'Reembolso completo registrado');
      setShowRefundModal(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar reembolso');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelOrder = async (order) => {
    if (!confirm('¿Cancelar esta orden? Los tickets serán invalidados.')) return;
    setActionLoading(true);
    try {
      await eventsApi.updateOrder(eventId, order.id, { status: 'cancelled' });
      toast.success('Orden cancelada');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!confirm(`¿Eliminar esta orden permanentemente? Se restockearán ${order.quantity || 1} entrada(s).`)) return;
    setActionLoading(true);
    try {
      const res = await eventsApi.deleteOrder(eventId, order.id);
      toast.success(res.data.message || 'Orden eliminada');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setActionLoading(false);
    }
  };

  // NEW: Fetch individual tickets for an order
  const fetchOrderTickets = async (orderId) => {
    setOrderTicketsLoading(true);
    try {
      const response = await api.get(`/events/${eventId}/orders/${orderId}/tickets`);
      setOrderTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching order tickets:', error);
      setOrderTickets([]);
    } finally {
      setOrderTicketsLoading(false);
    }
  };

  // NEW: Open order details and load tickets
  const handleOpenOrderDetails = (order) => {
    setSelectedOrder(order);
    setOrderTickets([]);
    setEditingTicketId(null);
    setShowOrderDetailsModal(true);
    fetchOrderTickets(order.id);
  };

  // NEW: Update individual ticket DNI
  const handleUpdateTicketDni = async (ticketId) => {
    if (!editingTicketDni.trim()) {
      toast.error('Ingresá un DNI válido');
      return;
    }
    setActionLoading(true);
    try {
      await api.put(`/events/${eventId}/orders/${selectedOrder.id}/tickets/${ticketId}`, {
        holder_dni: editingTicketDni
      });
      toast.success('DNI actualizado correctamente');
      setEditingTicketId(null);
      setEditingTicketDni('');
      fetchOrderTickets(selectedOrder.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar DNI');
    } finally {
      setActionLoading(false);
    }
  };


  const getStatusBadge = (status) => {
    const configs = {
      completed: { label: 'Completado', class: 'bg-green-500/20 text-green-400' },
      approved: { label: 'Aprobado', class: 'bg-green-500/20 text-green-400' },
      paid: { label: 'Pagado', class: 'bg-green-500/20 text-green-400' },
      confirmed: { label: 'Confirmado', class: 'bg-green-500/20 text-green-400' },
      pending: { label: 'Pendiente', class: 'bg-yellow-500/20 text-yellow-400' },
      cancelled: { label: 'Cancelado', class: 'bg-red-500/20 text-red-400' },
      refunded: { label: 'Reembolsado', class: 'bg-purple-500/20 text-purple-400' },
    };
    const config = configs[status] || { label: status, class: 'bg-zinc-500/20 text-zinc-400' };
    return <span className={`px-2 py-1 text-xs font-bold uppercase ${config.class}`}>{config.label}</span>;
  };

  // ========== REPORTS FUNCTIONS ==========
  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const [reportRes, historyRes] = await Promise.all([
        eventsApi.getReport(eventId).catch(() => ({ data: null })),
        eventsApi.getReportHistory(eventId).catch(() => ({ data: [] }))
      ]);
      setReport(reportRes.data);
      setReportHistory(historyRes.data || []);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setReportLoading(false);
    }
  };

  const handleSendReport = async (email = null) => {
    setActionLoading(true);
    try {
      await eventsApi.sendReport(eventId, email || customEmail || null);
      toast.success('Reporte enviado correctamente');
      setCustomEmail('');
      fetchReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar reporte');
    } finally {
      setActionLoading(false);
    }
  };

  // ========== RRPP FUNCTIONS ==========
  const fetchRRPP = async () => {
    setRRPPLoading(true);
    try {
      const res = await eventsApi.getRRPP(eventId);
      setRRPPs(res.data || []);
    } catch (error) {
      console.error('Error fetching RRPP:', error);
    } finally {
      setRRPPLoading(false);
    }
  };

  const handleCreateRRPP = async () => {
    if (!newRRPP.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setActionLoading(true);
    try {
      await eventsApi.createRRPP(eventId, newRRPP);
      toast.success('RRPP agregado');
      setShowRRPPModal(false);
      setNewRRPP({ name: '', email: '', phone: '', commission_percent: 10 });
      fetchRRPP();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar RRPP');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRRPP = async (rrppId) => {
    if (!confirm('¿Eliminar este RRPP?')) return;
    try {
      await eventsApi.deleteRRPP(eventId, rrppId);
      toast.success('RRPP eliminado');
      fetchRRPP();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  // ========== INVITATIONS FUNCTIONS ==========
  const fetchInvitations = async () => {
    setInvitationsLoading(true);
    try {
      const res = await eventsApi.getInvitations(eventId);
      setInvitations(res.data || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setInvitationsLoading(false);
    }
  };

  const handleSendInvitations = async () => {
    if (!inviteForm.emails.trim()) {
      toast.error('Ingresa al menos un email');
      return;
    }
    setActionLoading(true);
    try {
      const emails = inviteForm.emails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e);
      await eventsApi.sendInvitations(eventId, {
        emails,
        tier_id: inviteForm.tier_id || null,
        quantity_per_invite: inviteForm.quantity_per_invite
      });
      toast.success(`${emails.length} invitaciones enviadas`);
      setShowInviteModal(false);
      setInviteForm({ emails: '', tier_id: '', quantity_per_invite: 1 });
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar invitaciones');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await eventsApi.resendInvitation(eventId, invitationId);
      toast.success('Invitación reenviada');
    } catch (error) {
      toast.error('Error al reenviar');
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!confirm('¿Eliminar esta invitación?')) return;
    try {
      await eventsApi.deleteInvitation(eventId, invitationId);
      toast.success('Invitación eliminada');
      fetchInvitations();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Load tab data when switching
  useEffect(() => {
    if (activeTab === 'orders' && orders.length === 0) fetchOrders();
    if (activeTab === 'reports' && !report) fetchReport();
    if (activeTab === 'rrpp' && rrpps.length === 0) fetchRRPP();
    if (activeTab === 'invitations' && invitations.length === 0) fetchInvitations();
    if (activeTab === 'venue' && event?.venue_id && !venue && !venueLoading) {
      setVenueLoading(true);
      api.get(`/venues/public/${event.venue_id}`)
        .then(r => setVenue(r.data?.venue || r.data))
        .catch(() => toast.error('No se pudo cargar el venue'))
        .finally(() => setVenueLoading(false));
    }
  }, [activeTab, event?.venue_id]);

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#D4AF37]" size={48} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  const handleEditArtist = (artist) => {
    setEditingArtist(artist);
    setNewArtist(artist);
    setShowArtistModal(true);
  };

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/eventos')}
                className="hover:bg-zinc-800"
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">{event?.title}</h1>
                <p className="text-zinc-400 text-sm">Gestionar evento</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {(event?.status === 'published' || event?.status === 'completed' || event?.status === 'ended') && (
                <Button
                  onClick={() => navigate(`/admin/campanas/nueva?event=${eventId}`)}
                  className="bg-gradient-to-r from-[#D4AF37] to-[#F5D25A] hover:from-[#F5D25A] hover:to-[#D4AF37] text-black font-bold"
                  data-testid="admin-create-campaign-btn"
                  title="Crear una campaña de email para los compradores de este evento o cualquier otro segmento"
                >
                  <Megaphone size={16} className="mr-2" />
                  Crear campaña
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar Cambios
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-zinc-800 pb-4 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#D4AF37] text-black'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {/* Info Tab */}
            {activeTab === 'info' && (
              <motion.div
                key="info"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                {/* Left Column - Basic Info */}
                <div className="space-y-4">
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <Settings size={18} className="text-[#D4AF37]" />
                      Información General
                    </h3>
                    
                    <div>
                      <Label className="text-zinc-400">Nombre del Evento</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-zinc-400">Descripción</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        rows={4}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400">Fecha</Label>
                        <Input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Hora</Label>
                        <Input
                          type="time"
                          value={formData.time}
                          onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 mt-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-zinc-400">Ubicación</Label>
                      <Input
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        placeholder="Dirección del evento"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-zinc-400">Nombre del Venue</Label>
                      <Input
                        value={formData.venue_name}
                        onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        placeholder="Ej: Club Discepolo"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-zinc-400">Link de Google Maps</Label>
                      <Input
                        value={formData.google_maps_url}
                        onChange={(e) => setFormData({ ...formData, google_maps_url: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        placeholder="https://maps.google.com/..."
                      />
                    </div>
                  </div>
                </div>

                {/* Right Column - Image & Pricing */}
                <div className="space-y-4">
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <ImageIcon size={18} className="text-[#D4AF37]" />
                      Imagen del Evento
                    </h3>
                    
                    {formData.image_url && (
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    )}
                    
                    <CloudinaryUploader
                      onUploadComplete={(result) => setFormData({ ...formData, image_url: result.url })}
                      folder="bravo/events"
                      buttonText="Subir imagen"
                      showPreview={false}
                    />
                    
                    <div className="mt-3 pt-3 border-t border-zinc-800">
                      <Label className="text-zinc-400 text-xs">URL manual de imagen (OG / Redes Sociales)</Label>
                      <Input
                        value={formData.image_url || ''}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="https://res.cloudinary.com/..."
                        className="bg-zinc-800 border-zinc-700 mt-1 text-sm"
                        data-testid="event-image-url-input"
                      />
                      <p className="text-[10px] text-zinc-600 mt-1">
                        Esta URL se usa como imagen de vista previa al compartir en redes sociales
                      </p>
                    </div>
                  </div>

                  {/* YouTube Video Section */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <Youtube size={18} className="text-red-500" />
                      Video de YouTube (opcional)
                    </h3>
                    
                    <div>
                      <Label className="text-zinc-400 text-xs">URL del video de YouTube</Label>
                      <Input
                        value={formData.youtube_video_url || ''}
                        onChange={(e) => setFormData({ ...formData, youtube_video_url: e.target.value })}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="bg-zinc-800 border-zinc-700 mt-1 text-sm"
                        data-testid="event-youtube-url-input"
                      />
                    </div>
                    
                    {formData.youtube_video_url && (
                      <div className="aspect-video bg-zinc-800 rounded-lg overflow-hidden">
                        <iframe
                          src={`https://www.youtube.com/embed/${formData.youtube_video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1] || ''}`}
                          title="YouTube video preview"
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    )}
                    
                    <p className="text-[10px] text-zinc-600">
                      Este video reemplazará al flyer en la página pública del evento. En el listado de eventos seguirá mostrándose el flyer.
                    </p>
                  </div>

                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <Ticket size={18} className="text-[#D4AF37]" />
                      Configuración de Precios
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400">Cargo por Servicio</Label>
                        <Input
                          type="number"
                          value={formData.service_fee}
                          onChange={(e) => setFormData({ ...formData, service_fee: parseFloat(e.target.value) || 0 })}
                          className="bg-zinc-800 border-zinc-700 mt-1"
                          min={0}
                        />
                      </div>
                      <div className="hidden">
                        {/* Service fee type is now always percentage */}
                        <input type="hidden" value="percentage" />
                      </div>
                    </div>
                    
                    <p className="text-sm text-zinc-500">
                      Se agregará {formData.service_fee}% al precio de cada entrada
                    </p>
                  </div>
                  
                  {/* Virtual Queue Settings */}
                  <div className="bg-zinc-800/50 rounded-xl p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Users className="text-blue-400" size={18} />
                        </div>
                        <div>
                          <p className="text-white font-medium">Cola Virtual</p>
                          <p className="text-xs text-zinc-500">Gestionar tráfico en eventos de alta demanda</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.queue_enabled || false}
                        onCheckedChange={(checked) => setFormData({ ...formData, queue_enabled: checked })}
                      />
                    </div>
                    
                    {formData.queue_enabled && (
                      <div className="pt-3 border-t border-zinc-700 space-y-3">
                        <div>
                          <Label className="text-sm text-zinc-400">Usuarios simultáneos máximos</Label>
                          <Input
                            type="number"
                            value={formData.queue_max_concurrent || 50}
                            onChange={(e) => setFormData({ ...formData, queue_max_concurrent: parseInt(e.target.value) || 50 })}
                            className="bg-zinc-800 border-zinc-700 mt-1"
                            min={1}
                            max={500}
                          />
                          <p className="text-xs text-zinc-500 mt-1">
                            Cuántos usuarios pueden estar en el checkout al mismo tiempo
                          </p>
                          <p className="text-[11px] text-amber-300/80 mt-2">
                            💡 Para <strong>probar la sala de espera</strong> con un solo usuario, dejá este número en <strong>1</strong>. Así el segundo testeo va a verla. Para producción, valores típicos: 30–100.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Custom Terms & Conditions - admin can always edit and toggle the lock */}
                  <div className="bg-zinc-800/50 rounded-xl p-4 space-y-3" data-testid="admin-terms-card">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                          <FileText className="text-amber-400" size={18} />
                        </div>
                        <div>
                          <p className="text-white font-medium">Términos y condiciones del evento</p>
                          <p className="text-xs text-zinc-500">Aparecen en el checkout, opcionalmente en el mail de confirmación</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {formData.custom_terms_locked && event?.status !== 'draft' ? (
                          <span className="text-[10px] uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5 flex items-center gap-1">
                            <Lock size={9} /> Bloqueado p/ productora
                          </span>
                        ) : event?.status === 'draft' ? (
                          <span className="text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">
                            Borrador · editable
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <textarea
                      value={formData.custom_terms || ''}
                      onChange={(e) => setFormData({ ...formData, custom_terms: e.target.value })}
                      rows={5}
                      placeholder="Ej: El evento se realiza en una embarcación. Los asistentes declaran..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      data-testid="admin-custom-terms-input"
                    />
                    <p className="text-[11px] text-zinc-500">
                      Como admin podés modificar los términos aunque el evento esté publicado. Dejá vacío si no necesitás términos específicos.
                    </p>

                    <div className="grid sm:grid-cols-2 gap-2">
                      <label className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-colors ${formData.include_terms_in_email ? 'border-violet-500/50 bg-violet-500/5' : 'border-zinc-700 bg-zinc-900/40 hover:border-zinc-600'}`}>
                        <input
                          type="checkbox"
                          checked={!!formData.include_terms_in_email}
                          onChange={(e) => setFormData({ ...formData, include_terms_in_email: e.target.checked })}
                          disabled={!formData.custom_terms?.trim()}
                          className="mt-0.5 accent-violet-500"
                          data-testid="admin-include-terms-in-email"
                        />
                        <div>
                          <p className="text-xs text-white font-medium">📧 Incluir T&amp;C en el email</p>
                          <p className="text-[10px] text-zinc-400 leading-snug">Útil como prueba ante disputas con Mercado Pago.</p>
                        </div>
                      </label>

                      <label className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-colors ${formData.custom_terms_locked ? 'border-amber-500/40 bg-amber-500/5' : 'border-zinc-700 bg-zinc-900/40 hover:border-zinc-600'}`}>
                        <input
                          type="checkbox"
                          checked={!!formData.custom_terms_locked}
                          onChange={(e) => setFormData({ ...formData, custom_terms_locked: e.target.checked })}
                          className="mt-0.5 accent-amber-500"
                          data-testid="admin-terms-lock-toggle"
                        />
                        <div>
                          <p className="text-xs text-white font-medium flex items-center gap-1"><Lock size={10} /> Bloquear para la productora</p>
                          <p className="text-[10px] text-zinc-400 leading-snug">Si está activo, la productora no podrá modificarlos desde su portal. Sólo admin.</p>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {/* Cancel Event Button */}
                  {event?.status !== 'cancelled' && !event?.is_locked && (
                    <Button
                      onClick={() => setShowCancelModal(true)}
                      variant="outline"
                      className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    >
                      <XCircle size={16} className="mr-2" />
                      Cancelar Evento
                    </Button>
                  )}
                  
                  {/* Locked/Cancelled Event Notice */}
                  {(event?.status === 'cancelled' || event?.is_locked) && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
                      <Lock className="text-red-400" size={20} />
                      <div>
                        <p className="text-red-400 font-medium">Evento cancelado</p>
                        <p className="text-red-400/70 text-sm">Este evento está bloqueado y no aparece en la página pública.</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Tickets Tab */}
            {activeTab === 'tickets' && (
              <motion.div
                key="tickets"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-white">Tandas de Entradas</h3>
                    <p className="text-[10px] text-emerald-400/80 mt-0.5">⚡ Stock se sincroniza automáticamente cada 2 minutos</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        try {
                          const res = await api.post(`/events/${eventId}/tiers/sync-sold`);
                          toast.success(`Stock sincronizado: ${res.data.updated_tiers?.length || 0} tandas actualizadas`);
                          fetchEventData();
                        } catch (err) {
                          toast.error('Error al sincronizar stock');
                        }
                      }}
                      variant="outline"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      title="Forzá una sincronización manual ahora. (El sistema lo hace solo cada 2 min)"
                    >
                      <RefreshCw size={16} className="mr-2" />
                      Sincronizar Ahora
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingTicket(null);
                        setNewTicket({ name: '', price: 0, quantity: 100, description: '', is_active: true, free_service_for_subscribers: false, tier_type: 'sale', inquiry_label: '', is_sold_out: false, max_per_order: 10, price_stages: [] });
                        setShowTicketModal(true);
                      }}
                      className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                    >
                      <Plus size={16} className="mr-2" />
                      Nueva Tanda
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tickets.map((ticket) => (
                    <div key={ticket.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-bold text-white">{ticket.name}</h4>
                          <p className="text-2xl font-bold text-[#D4AF37]">${ticket.price?.toLocaleString()}</p>
                          <p className="text-xs text-zinc-500">
                            + {formData.service_fee_type === 'percentage' ? `${formData.service_fee}%` : `$${formData.service_fee}`} servicio
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-1 rounded text-xs ${ticket.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {ticket.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                          {(ticket.price_stages || []).length > 0 && (
                            <span className="px-2 py-1 rounded text-xs bg-[#D4AF37]/20 text-[#D4AF37] flex items-center gap-1" title={`${ticket.price_stages.length} tandas configuradas`}>
                              🎯 {ticket.price_stages.length} tandas
                            </span>
                          )}
                          {ticket.tier_type === 'inquiry' && (
                            <span className="px-2 py-1 rounded text-xs bg-cyan-500/20 text-cyan-400 flex items-center gap-1">
                              💬 Consultar
                            </span>
                          )}
                          {ticket.promo_pay_x > 0 && ticket.promo_get_y > ticket.promo_pay_x && (
                            <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 flex items-center gap-1" title={`Promo: pagás ${ticket.promo_pay_x}, llevás ${ticket.promo_get_y}`} data-testid={`admin-manage-promo-badge-${ticket.id}`}>
                              🎁 Promo {ticket.promo_get_y}x{ticket.promo_pay_x}
                            </span>
                          )}
                          {ticket.free_service_for_subscribers && (
                            <span className="px-2 py-1 rounded text-xs bg-[#D4AF37]/20 text-[#D4AF37] flex items-center gap-1">
                              <Users size={10} />
                              BRAVX
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-zinc-400 mb-3">
                        Vendidos: {ticket.sold || 0} / {ticket.quantity}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingTicket(ticket);
                            setNewTicket({
                              ...ticket,
                              tier_type: ticket.tier_type || 'sale',
                              inquiry_label: ticket.inquiry_label || '',
                              is_sold_out: !!ticket.is_sold_out,
                              max_per_order: ticket.max_per_order || 10,
                              price_stages: Array.isArray(ticket.price_stages) ? ticket.price_stages : [],
                            });
                            setShowTicketModal(true);
                          }}
                          className="flex-1 border-zinc-700"
                        >
                          <Edit2 size={14} className="mr-1" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteTicket(ticket.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {tickets.length === 0 && (
                  <div className="text-center py-12 text-zinc-500">
                    <Ticket size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No hay tandas de entradas</p>
                    <p className="text-sm">Crea una para empezar a vender</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Venue Tab */}
            {activeTab === 'venue' && (
              <motion.div
                key="venue"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <div className="flex justify-between items-center flex-wrap gap-3">
                  <div>
                    <h3 className="font-bold text-white text-lg">Venue del evento</h3>
                    <p className="text-zinc-500 text-sm">Visualización y gestión del mapa interactivo de butacas/sectores.</p>
                  </div>
                  {event?.venue_id && (
                    <Button
                      onClick={() => window.open(`/producer/venues`, '_blank')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="admin-venue-edit-btn"
                    >
                      <ExternalLink size={16} className="mr-2" />
                      Editar venue
                    </Button>
                  )}
                </div>

                {!event?.venue_id ? (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center" data-testid="admin-venue-not-linked">
                    <MapPin size={42} className="mx-auto text-zinc-700 mb-3" />
                    <h4 className="font-bold text-white text-base mb-1">No hay venue asociado</h4>
                    <p className="text-zinc-500 text-sm mb-5 max-w-md mx-auto">
                      Este evento todavía no tiene un mapa interactivo. Creá o asigná un venue desde el portal productora para habilitar la selección visual de zonas.
                    </p>
                    <Button
                      onClick={() => window.open(`/producer/venues`, '_blank')}
                      className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                    >
                      <ExternalLink size={16} className="mr-2" />
                      Abrir gestor de venues
                    </Button>
                  </div>
                ) : venueLoading ? (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-16 flex items-center justify-center">
                    <Loader2 className="animate-spin text-zinc-500" size={28} />
                  </div>
                ) : venue ? (
                  <div className="space-y-4">
                    {/* Venue info cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                        <p className="text-zinc-500 text-xs uppercase tracking-wider">Nombre</p>
                        <p className="text-base font-bold text-white truncate">{venue.name || '—'}</p>
                      </div>
                      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                        <p className="text-zinc-500 text-xs uppercase tracking-wider">Capacidad</p>
                        <p className="text-base font-bold text-white">{venue.capacity || (venue.sections || []).reduce((s, x) => s + (x.capacity || 0), 0)}</p>
                      </div>
                      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                        <p className="text-zinc-500 text-xs uppercase tracking-wider">Sectores</p>
                        <p className="text-base font-bold text-white">{(venue.sections || []).filter(s => s.type === 'section' || s.type === 'lounge' || s.type === 'bar' || s.type === 'table').length}</p>
                      </div>
                      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                        <p className="text-zinc-500 text-xs uppercase tracking-wider">Elementos totales</p>
                        <p className="text-base font-bold text-white">{(venue.sections || []).length}</p>
                      </div>
                    </div>

                    {/* Full Demand Intelligence panel (same as producer view, read-only) */}
                    <VenueStatsTab eventId={eventId} event={event} apiMode="admin" />
                  </div>
                ) : (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center">
                    <p className="text-zinc-500">No se pudo cargar el venue.</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Lineup Tab */}
            {activeTab === 'lineup' && (
              <motion.div
                key="lineup"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-white">Line-up del Evento</h3>
                  <Button
                    onClick={() => {
                      setEditingArtist(null);
                      setNewArtist({ name: '', image_url: '', description: '', schedule: '', stage: '' });
                      setShowArtistModal(true);
                    }}
                    className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                  >
                    <Plus size={16} className="mr-2" />
                    Agregar Artista
                  </Button>
                </div>

                {artists.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={artists.map(a => a.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-3">
                        {artists.map((artist) => (
                          <SortableArtistItem key={artist.id} artist={artist} onEdit={handleEditArtist} onDelete={handleDeleteArtist} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <Music size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No hay artistas en el lineup</p>
                    <p className="text-sm">Agrega artistas para completar el evento</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* ORDERS Tab */}
            {activeTab === 'orders' && (
              <motion.div
                key="orders"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                {/* Stats - Only count valid (non-cancelled) orders */}
                {(() => {
                  const validStatuses = ['completed', 'approved', 'paid', 'confirmed'];
                  const validOrders = orders.filter(o => validStatuses.includes(o.status));
                  const cancelledCount = orders.filter(o => o.status === 'cancelled').length;
                  
                  const totalTickets = validOrders.reduce((sum, o) => sum + (o.quantity || 0), 0);
                  const subtotalGross = validOrders.reduce((sum, o) => {
                    const qty = o.quantity || 1;
                    const unitPrice = o.unit_price || 0;
                    return sum + (o.subtotal || (unitPrice * qty));
                  }, 0);
                  const discountTotal = validOrders.reduce((sum, o) => sum + (o.discount_amount || 0), 0);
                  const netRevenue = Math.max(0, subtotalGross - discountTotal);
                  const totalRevenue = validOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
                  const showDiscountCard = discountTotal > 0;
                  
                  return (
                    <div className={`grid grid-cols-2 ${showDiscountCard ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
                      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                        <p className="text-zinc-500 text-sm">Total Órdenes</p>
                        <p className="text-2xl font-black">{validOrders.length}</p>
                        {cancelledCount > 0 && (
                          <p className="text-xs text-red-400">{cancelledCount} canceladas (no incluidas)</p>
                        )}
                      </div>
                      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                        <p className="text-zinc-500 text-sm">Tickets Vendidos</p>
                        <p className="text-2xl font-black">{totalTickets}</p>
                      </div>
                      <div className="bg-zinc-900 border border-emerald-500/30 rounded-xl p-4" data-testid="admin-orders-net-revenue-card">
                        <p className="text-emerald-400 text-sm">Ingreso Neto</p>
                        <p className="text-2xl font-black text-emerald-400">
                          ${netRevenue.toLocaleString()}
                        </p>
                        <p className="text-xs text-zinc-500">Sin costos de servicio · Descuentos restados</p>
                      </div>
                      {showDiscountCard && (
                        <div className="bg-zinc-900 border border-amber-500/30 rounded-xl p-4" data-testid="admin-orders-discount-card">
                          <p className="text-amber-400 text-sm">Descuentos Aplicados</p>
                          <p className="text-2xl font-black text-amber-400">
                            −${discountTotal.toLocaleString()}
                          </p>
                          <p className="text-xs text-zinc-500">Cupones canjeados</p>
                        </div>
                      )}
                      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                        <p className="text-zinc-500 text-sm">Total Recaudado</p>
                        <p className="text-2xl font-black">${totalRevenue.toLocaleString()}</p>
                        <p className="text-xs text-zinc-500">Con costos de servicio</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Actions Bar */}
                <div className="flex items-center justify-between">
                  <Input
                    placeholder="Buscar por email, nombre..."
                    className="bg-zinc-900 border-zinc-800 w-64"
                  />
                  <Button variant="outline" className="border-zinc-700" onClick={fetchOrders}>
                    <RefreshCw size={14} className="mr-2" />
                    Actualizar
                  </Button>
                </div>

                {/* Orders Table */}
                {ordersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin" size={32} />
                  </div>
                ) : orders.length > 0 ? (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead className="border-b border-zinc-800">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs uppercase text-zinc-500">ID</th>
                          <th className="text-left py-3 px-4 text-xs uppercase text-zinc-500">Comprador</th>
                          <th className="text-left py-3 px-4 text-xs uppercase text-zinc-500">Cant.</th>
                          <th className="text-left py-3 px-4 text-xs uppercase text-zinc-500">Subtotal</th>
                          <th className="text-left py-3 px-4 text-xs uppercase text-zinc-500">Serv.</th>
                          <th className="text-left py-3 px-4 text-xs uppercase text-zinc-500">Total</th>
                          <th className="text-left py-3 px-4 text-xs uppercase text-zinc-500">Estado</th>
                          <th className="text-right py-3 px-4 text-xs uppercase text-zinc-500">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => {
                          // Calculate subtotal and service fee
                          const quantity = order.quantity || 1;
                          const unitPrice = order.unit_price || 0;
                          const subtotal = order.subtotal || (unitPrice * quantity);
                          const serviceFee = order.service_fee || 0;
                          // Handle legacy orders where service_fee might be per-unit
                          const totalServiceFee = serviceFee < unitPrice && quantity > 1 
                            ? serviceFee * quantity 
                            : serviceFee;
                          const totalAmount = order.total_amount || 0;
                          const discountAmount = order.discount_amount || 0;
                          const discountCode = order.discount?.code || order.discount_code || null;
                          const netSubtotal = Math.max(0, subtotal - discountAmount);
                          
                          return (
                            <tr key={order.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                              <td className="py-3 px-4">
                                <code className="text-xs bg-zinc-800 px-1">{order.id?.slice(0, 8)}...</code>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium">{order.buyer_name || '-'}</p>
                                  {discountAmount > 0 && (
                                    <span
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-bold uppercase tracking-wider"
                                      data-testid={`admin-order-discount-badge-${order.id}`}
                                      title={discountCode ? `Código: ${discountCode}` : 'Descuento aplicado'}
                                    >
                                      <Tag size={10} />
                                      Descuento {discountCode ? `· ${discountCode}` : ''}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-zinc-500">{order.buyer_email}</p>
                              </td>
                              <td className="py-3 px-4 font-mono">{quantity}</td>
                              <td className="py-3 px-4 font-mono">
                                {discountAmount > 0 ? (
                                  <>
                                    <p className="text-zinc-500 line-through text-[11px] leading-tight">${subtotal.toLocaleString()}</p>
                                    <p className="text-emerald-400 leading-tight">${netSubtotal.toLocaleString()}</p>
                                    <p className="text-amber-400 text-[10px] leading-tight">−${discountAmount.toLocaleString()}</p>
                                  </>
                                ) : (
                                  <span className="text-emerald-400">${subtotal.toLocaleString()}</span>
                                )}
                              </td>
                              <td className="py-3 px-4 font-mono text-zinc-500">${totalServiceFee.toLocaleString()}</td>
                              <td className="py-3 px-4 font-mono font-bold">${totalAmount.toLocaleString()}</td>
                              <td className="py-3 px-4">{getStatusBadge(order.status)}</td>
                              <td className="py-3 px-4 text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm"><MoreHorizontal size={16} /></Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-zinc-950 border-zinc-800">
                                    <DropdownMenuItem onClick={() => handleOpenOrderDetails(order)}>
                                      <Eye size={14} className="mr-2" />Ver Detalles
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleEditOrder(order)}>
                                      <Edit2 size={14} className="mr-2" />Editar Orden
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleResendTickets(order)}>
                                      <Send size={14} className="mr-2" />Reenviar Tickets
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="bg-zinc-800" />
                                    <DropdownMenuItem onClick={() => handleOpenRefund(order)}>
                                      <DollarSign size={14} className="mr-2" />Reembolsar
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleCancelOrder(order)} className="text-red-400">
                                      <XCircle size={14} className="mr-2" />Cancelar Orden
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDeleteOrder(order)} className="text-red-400" data-testid={`delete-order-${order.id}`}>
                                      <Trash2 size={14} className="mr-2" />Eliminar Orden
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <ShoppingCart size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No hay órdenes para este evento</p>
                    <p className="text-sm">Las órdenes aparecerán cuando los usuarios compren tickets</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* REPORTS Tab */}
            {activeTab === 'reports' && (
              <motion.div
                key="reports"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                {/* Stats */}
                {report && (
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                      <Ticket size={24} className="mx-auto mb-2 text-blue-500" />
                      <p className="text-2xl font-black">{report.summary?.total_tickets_sold || 0}</p>
                      <p className="text-xs text-zinc-500 uppercase">Tickets Vendidos</p>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                      <Users size={24} className="mx-auto mb-2 text-green-500" />
                      <p className="text-2xl font-black">{report.summary?.checked_in || 0}</p>
                      <p className="text-xs text-zinc-500 uppercase">Asistentes</p>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                      <DollarSign size={24} className="mx-auto mb-2 text-[#D4AF37]" />
                      <p className="text-2xl font-black">${(report.summary?.total_revenue || 0).toLocaleString()}</p>
                      <p className="text-xs text-zinc-500 uppercase">Ingreso Bruto</p>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                      <CheckCircle size={24} className="mx-auto mb-2 text-purple-500" />
                      <p className="text-2xl font-black">{report.summary?.attendance_rate || 0}%</p>
                      <p className="text-xs text-zinc-500 uppercase">Tasa Asistencia</p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <Button variant="outline" className="border-zinc-700" onClick={() => setShowReportModal(true)}>
                    <Eye size={14} className="mr-2" />
                    Ver Reporte Completo
                  </Button>
                  <Button className="bg-[#D4AF37] hover:bg-[#B8962E] text-black" onClick={() => handleSendReport()} disabled={actionLoading}>
                    {actionLoading ? <Loader2 className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}
                    Enviar a Productora
                  </Button>
                </div>

                {/* Custom Email */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <h4 className="text-white font-bold flex items-center gap-2 mb-3">
                    <Mail size={16} />
                    Enviar a Email Personalizado
                  </h4>
                  <div className="flex gap-3">
                    <Input
                      value={customEmail}
                      onChange={(e) => setCustomEmail(e.target.value)}
                      placeholder="email@ejemplo.com"
                      className="bg-zinc-800 border-zinc-700 flex-1"
                    />
                    <Button onClick={() => handleSendReport(customEmail)} disabled={actionLoading || !customEmail}>
                      <Send size={14} />
                    </Button>
                  </div>
                </div>

                {/* History */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <h4 className="text-white font-bold flex items-center gap-2 mb-3">
                    <Clock size={16} />
                    Historial de Reportes
                  </h4>
                  {reportHistory.length > 0 ? (
                    <div className="space-y-2">
                      {reportHistory.map((item) => (
                        <div key={item.id} className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
                          <div>
                            <p className="font-medium">{item.sent_to}</p>
                            <p className="text-xs text-zinc-500">{item.sent_at && new Date(item.sent_at).toLocaleString()}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs uppercase ${
                            item.type === 'automatic' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {item.type === 'automatic' ? 'Auto' : 'Manual'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-center py-4">No hay reportes enviados</p>
                  )}
                </div>
              </motion.div>
            )}

            {/* INVITATIONS Tab */}
            {activeTab === 'invitations' && (
              <motion.div
                key="invitations"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-white">Invitaciones</h3>
                  <Button onClick={() => setShowInviteModal(true)} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black">
                    <Plus size={16} className="mr-2" />
                    Enviar Invitaciones
                  </Button>
                </div>

                {invitationsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin" size={32} />
                  </div>
                ) : invitations.length > 0 ? (
                  <div className="space-y-3">
                    {invitations.map((inv) => (
                      <div key={inv.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{inv.email}</p>
                          <p className="text-xs text-zinc-500">
                            Código: <code className="bg-zinc-800 px-1">{inv.code}</code>
                            {inv.tier_name && ` • ${inv.tier_name}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 text-xs uppercase ${
                            inv.status === 'used' ? 'bg-green-500/20 text-green-400' : 
                            inv.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {inv.status === 'used' ? 'Usado' : inv.status === 'pending' ? 'Pendiente' : inv.status}
                          </span>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleResendInvitation(inv.id)}>
                              <Send size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteInvitation(inv.id)} className="text-red-400">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <Mail size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No hay invitaciones enviadas</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* RRPP Tab */}
            {activeTab === 'rrpp' && (
              <motion.div
                key="rrpp"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-white">Equipo RRPP</h3>
                  <Button onClick={() => setShowRRPPModal(true)} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black">
                    <Plus size={16} className="mr-2" />
                    Agregar RRPP
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                    <Users size={24} className="mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-black">{rrpps.length}</p>
                    <p className="text-xs text-zinc-500 uppercase">Total RRPP</p>
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                    <Ticket size={24} className="mx-auto mb-2 text-green-500" />
                    <p className="text-2xl font-black">{rrpps.reduce((sum, r) => sum + (r.sales_count || 0), 0)}</p>
                    <p className="text-xs text-zinc-500 uppercase">Ventas Totales</p>
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                    <DollarSign size={24} className="mx-auto mb-2 text-[#D4AF37]" />
                    <p className="text-2xl font-black">${rrpps.reduce((sum, r) => sum + (r.total_sales_amount || 0), 0).toLocaleString()}</p>
                    <p className="text-xs text-zinc-500 uppercase">Ingresos</p>
                  </div>
                </div>

                {rrppLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin" size={32} />
                  </div>
                ) : rrpps.length > 0 ? (
                  <div className="space-y-4">
                    {rrpps.map((rrpp) => (
                      <div key={rrpp.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-bold">{rrpp.name}</h4>
                              <span className="px-2 py-1 text-xs bg-purple-500/20 text-purple-400 font-mono">{rrpp.code}</span>
                            </div>
                            <div className="flex gap-4 text-sm text-zinc-400 mb-3">
                              {rrpp.email && <span>{rrpp.email}</span>}
                              {rrpp.phone && <span>{rrpp.phone}</span>}
                              <span className="flex items-center gap-1"><Percent size={12} /> {rrpp.commission_percent}%</span>
                            </div>
                            <div className="flex gap-6">
                              <div><p className="text-xs text-zinc-600">Ventas</p><p className="font-black">{rrpp.sales_count || 0}</p></div>
                              <div><p className="text-xs text-zinc-600">Ingresos</p><p className="font-black">${(rrpp.total_sales_amount || 0).toLocaleString()}</p></div>
                              <div><p className="text-xs text-zinc-600">Comisión</p><p className="font-black text-green-400">${(rrpp.commission_earned || 0).toLocaleString()}</p></div>
                            </div>
                            {rrpp.sale_link && (
                              <div className="mt-3 p-2 bg-zinc-800/50 rounded flex items-center gap-2">
                                <code className="flex-1 text-xs text-zinc-500 truncate">{rrpp.sale_link}</code>
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(rrpp.sale_link)}><Copy size={12} /></Button>
                                <a href={rrpp.sale_link} target="_blank" rel="noopener noreferrer" className="p-1 hover:text-[#D4AF37]"><ExternalLink size={12} /></a>
                              </div>
                            )}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteRRPP(rrpp.id)} className="text-red-400"><Trash2 size={16} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <Users size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No hay RRPP asignados</p>
                    <p className="text-sm">Agrega RRPP para que vendan entradas con comisión</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Heatmap Tab */}
            {activeTab === 'heatmap' && (
              <motion.div
                key="heatmap"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <HeatmapTab eventId={eventId} apiMode="admin" />
              </motion.div>
            )}

            {/* Co-production Tab */}
            {activeTab === 'co_production' && (
              <motion.div
                key="co_production"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <CoProductionTab eventId={eventId} apiMode="admin" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Ticket Modal */}
          <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
            <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingTicket ? 'Editar Entrada' : 'Nueva Entrada'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Tier Type */}
                <div>
                  <Label className="text-zinc-400 mb-2 block">Tipo de tanda</Label>
                  <div className="grid grid-cols-2 gap-2" data-testid="admin-tier-type-picker">
                    {[
                      { id: 'sale', label: 'Venta online', desc: 'Compra directa con MP', icon: '🛒' },
                      { id: 'inquiry', label: 'Consultar', desc: 'Chat in-platform', icon: '💬' },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`admin-tier-type-${opt.id}`}
                        onClick={() => setNewTicket({ ...newTicket, tier_type: opt.id })}
                        className={`p-3 rounded-lg border text-left transition-all ${(newTicket.tier_type || 'sale') === opt.id ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-zinc-700 hover:border-zinc-600'}`}
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-lg">{opt.icon}</span>
                          <span className="text-sm text-white font-medium">{opt.label}</span>
                        </div>
                        <p className="text-[10px] text-zinc-500">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-400">Nombre de la Tanda</Label>
                  <Input
                    value={newTicket.name}
                    onChange={(e) => setNewTicket({ ...newTicket, name: e.target.value })}
                    placeholder={(newTicket.tier_type || 'sale') === 'inquiry' ? 'Ej: Mesa VIP (8 personas)' : 'Ej: Early Bird, General, VIP'}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>

                {/* Inquiry-only fields */}
                {newTicket.tier_type === 'inquiry' && (
                  <div className="space-y-3 p-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5" data-testid="admin-inquiry-block">
                    <p className="text-xs text-cyan-200 leading-relaxed">
                      Esta opción no se vende online. Cuando un cliente la elija, se inicia un chat <strong>dentro de la plataforma</strong>. Las consultas aparecen en el tab &quot;Consultas&quot; del evento (en el portal productora).
                    </p>
                    <div>
                      <Label className="text-zinc-400">Texto del botón (opcional)</Label>
                      <Input
                        value={newTicket.inquiry_label || ''}
                        onChange={(e) => setNewTicket({ ...newTicket, inquiry_label: e.target.value })}
                        placeholder='Ej: Consultar disponibilidad'
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                      <p className="text-[10px] text-zinc-500 mt-1">Si lo dejás vacío, decimos &quot;Iniciar consulta&quot;.</p>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer p-2 rounded-md hover:bg-white/5">
                      <input
                        type="checkbox"
                        checked={!!newTicket.is_sold_out}
                        onChange={(e) => setNewTicket({ ...newTicket, is_sold_out: e.target.checked })}
                        className="mt-0.5 accent-emerald-500"
                        data-testid="admin-inquiry-soldout-toggle"
                      />
                      <div>
                        <p className="text-xs text-white font-medium">Marcar como agotada</p>
                        <p className="text-[10px] text-zinc-400 leading-snug">Si ya vendiste todos los cupos, activá esto para que aparezca como &quot;Agotado&quot; en el público y no acepte más consultas.</p>
                      </div>
                    </label>
                  </div>
                )}

                {(newTicket.tier_type || 'sale') === 'sale' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">Precio ($)</Label>
                      <Input
                        type="number"
                        value={newTicket.price}
                        onChange={(e) => setNewTicket({ ...newTicket, price: parseFloat(e.target.value) || 0 })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        min={0}
                        disabled={(newTicket.price_stages || []).length > 0}
                      />
                      {(newTicket.price_stages || []).length > 0 && (
                        <p className="text-[10px] text-[#D4AF37] mt-1">Usando precio por etapas ↓</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-zinc-400">Cantidad</Label>
                      <Input
                        type="number"
                        value={newTicket.quantity}
                        onChange={(e) => setNewTicket({ ...newTicket, quantity: parseInt(e.target.value) || 0 })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        min={1}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-zinc-400">Máximo por orden</Label>
                  <Input
                    type="number"
                    value={newTicket.max_per_order || 10}
                    onChange={(e) => setNewTicket({ ...newTicket, max_per_order: parseInt(e.target.value) || 1 })}
                    min={1}
                    max={20}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>

                <div>
                  <Label className="text-zinc-400">Descripción (opcional)</Label>
                  <Textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                    rows={2}
                  />
                </div>

                {/* Multi-stage Pricing (only for sale-type tiers) */}
                {(newTicket.tier_type || 'sale') === 'sale' && (
                  <div className="p-3 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 space-y-3" data-testid="admin-price-stages-block">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-[#D4AF37] font-medium flex items-center gap-1.5">
                          🎯 Precios por etapas (Tandas)
                          <span className="text-[9px] uppercase tracking-wider text-[#D4AF37]/80 bg-[#D4AF37]/15 border border-[#D4AF37]/30 rounded-full px-1.5 py-0.5">Opcional</span>
                        </div>
                        <p className="text-[10px] text-zinc-400 mt-0.5 leading-snug">Cuando se agota una tanda, se activa la siguiente automáticamente.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setNewTicket({
                          ...newTicket,
                          price_stages: [...(newTicket.price_stages || []), { label: `Etapa ${(newTicket.price_stages?.length || 0) + 1}`, price: newTicket.price || 0, quantity: 0, starts_at: '', ends_at: '' }]
                        })}
                        className="text-xs px-2 py-1 rounded-md bg-[#D4AF37] hover:bg-[#F5D25A] text-black font-bold"
                        data-testid="admin-add-stage-btn"
                      >
                        + Etapa
                      </button>
                    </div>

                    {/* Visual timeline */}
                    {(newTicket.price_stages || []).length > 0 && (
                      <div className="flex items-center gap-1 py-2 overflow-x-auto" data-testid="admin-stages-timeline">
                        {(newTicket.price_stages || []).map((stage, i) => (
                          <div key={i} className="flex items-center gap-1 flex-shrink-0">
                            <div className="flex flex-col items-center min-w-[58px]">
                              <div className="w-6 h-6 rounded-full bg-[#D4AF37] text-black text-[10px] font-bold flex items-center justify-center">{i + 1}</div>
                              <span className="text-[9px] text-zinc-400 mt-0.5 max-w-[60px] truncate text-center">{stage.label || `Etapa ${i + 1}`}</span>
                              <span className="text-[9px] text-[#D4AF37]">${Number(stage.price || 0).toLocaleString()}</span>
                            </div>
                            {i < (newTicket.price_stages.length - 1) && (
                              <div className="flex-1 min-w-[20px] h-[2px] bg-gradient-to-r from-[#D4AF37] via-[#D4AF37]/40 to-[#D4AF37]" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {(newTicket.price_stages || []).map((stage, idx) => (
                      <div key={idx} className="rounded-md border border-zinc-700 bg-zinc-900/60 p-2 space-y-1.5" data-testid={`admin-stage-${idx}`}>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] uppercase text-[#D4AF37] font-bold bg-[#D4AF37]/15 rounded px-1.5 py-0.5">#{idx + 1}</span>
                          <Input
                            value={stage.label || ''}
                            onChange={(e) => {
                              const stages = [...newTicket.price_stages];
                              stages[idx] = { ...stages[idx], label: e.target.value };
                              setNewTicket({ ...newTicket, price_stages: stages });
                            }}
                            placeholder='Ej: Preventa, Segunda tanda, Última'
                            className="bg-zinc-800 border-zinc-700 h-7 text-xs flex-1"
                          />
                          <button
                            type="button"
                            onClick={() => setNewTicket({ ...newTicket, price_stages: newTicket.price_stages.filter((_, i) => i !== idx) })}
                            className="text-red-400 hover:text-red-300 text-xs px-1"
                            data-testid={`admin-remove-stage-${idx}`}
                          >✕</button>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <Input
                            type="number"
                            value={stage.price || 0}
                            onChange={(e) => {
                              const stages = [...newTicket.price_stages];
                              stages[idx] = { ...stages[idx], price: parseFloat(e.target.value) || 0 };
                              setNewTicket({ ...newTicket, price_stages: stages });
                            }}
                            placeholder="Precio $"
                            className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                          />
                          <Input
                            type="number"
                            value={stage.quantity || 0}
                            onChange={(e) => {
                              const stages = [...newTicket.price_stages];
                              stages[idx] = { ...stages[idx], quantity: parseInt(e.target.value) || 0 };
                              setNewTicket({ ...newTicket, price_stages: stages });
                            }}
                            placeholder="Cantidad"
                            className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <Input
                            type="datetime-local"
                            value={stage.starts_at ? stage.starts_at.slice(0, 16) : ''}
                            onChange={(e) => {
                              const stages = [...newTicket.price_stages];
                              stages[idx] = { ...stages[idx], starts_at: e.target.value ? new Date(e.target.value).toISOString() : '' };
                              setNewTicket({ ...newTicket, price_stages: stages });
                            }}
                            className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                            title="Inicio de la etapa (opcional)"
                          />
                          <Input
                            type="datetime-local"
                            value={stage.ends_at ? stage.ends_at.slice(0, 16) : ''}
                            onChange={(e) => {
                              const stages = [...newTicket.price_stages];
                              stages[idx] = { ...stages[idx], ends_at: e.target.value ? new Date(e.target.value).toISOString() : '' };
                              setNewTicket({ ...newTicket, price_stages: stages });
                            }}
                            className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                            title="Fin de la etapa (opcional)"
                          />
                        </div>
                      </div>
                    ))}
                    {(newTicket.price_stages?.length || 0) === 0 && (
                      <p className="text-[10px] text-zinc-500 italic text-center py-2">Sin etapas — se usa el precio único de arriba.</p>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="ticket_active"
                    checked={newTicket.is_active}
                    onChange={(e) => setNewTicket({ ...newTicket, is_active: e.target.checked })}
                    className="rounded border-zinc-700"
                  />
                  <Label htmlFor="ticket_active" className="text-zinc-400">Activo (disponible para venta)</Label>
                </div>

                {/* FULL BRAVX Benefit - Admin Only */}
                <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                        <Users size={16} className="text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">Beneficio FULL BRAVX</p>
                        <p className="text-zinc-500 text-xs">Sin cargo por servicio para suscriptores</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newTicket.free_service_for_subscribers}
                        onChange={(e) => setNewTicket({ ...newTicket, free_service_for_subscribers: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF37]"></div>
                    </label>
                  </div>
                </div>

                {/* Promo "Pagá X, Llevás Y" - Admin Only */}
                {(newTicket.tier_type || 'sale') === 'sale' && (
                  <div className="bg-gradient-to-r from-green-500/10 to-transparent border border-green-500/30 rounded-lg p-4 space-y-3" data-testid="admin-manage-promo-section">
                    <div>
                      <p className="text-white text-sm font-medium">🎁 Promoción "Pagá X, Llevás Y"</p>
                      <p className="text-zinc-500 text-xs">Ej: pagás 1, llevás 2 (2x1). Se aplica automáticamente en el checkout.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400 text-xs">Pagás</Label>
                        <Input
                          type="number"
                          min={0}
                          value={newTicket.promo_pay_x ?? 0}
                          onChange={(e) => setNewTicket({ ...newTicket, promo_pay_x: parseInt(e.target.value) || 0 })}
                          placeholder="Ej: 1"
                          className="bg-zinc-800 border-zinc-700 mt-1"
                          data-testid="admin-manage-promo-pay-x"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Llevás</Label>
                        <Input
                          type="number"
                          min={0}
                          value={newTicket.promo_get_y ?? 0}
                          onChange={(e) => setNewTicket({ ...newTicket, promo_get_y: parseInt(e.target.value) || 0 })}
                          placeholder="Ej: 2"
                          className="bg-zinc-800 border-zinc-700 mt-1"
                          data-testid="admin-manage-promo-get-y"
                        />
                      </div>
                    </div>
                    {Number(newTicket.promo_pay_x) > 0 && Number(newTicket.promo_get_y) > Number(newTicket.promo_pay_x) && (
                      <p className="text-[11px] text-green-400">
                        Preview: el comprador paga {newTicket.promo_pay_x} y recibe {newTicket.promo_get_y} ({Number(newTicket.promo_get_y) - Number(newTicket.promo_pay_x)} de regalo, ingresa un DNI por cada entrada).
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400 text-xs">Vigente hasta (opcional)</Label>
                        <Input
                          type="datetime-local"
                          value={newTicket.promo_valid_until || ''}
                          onChange={(e) => setNewTicket({ ...newTicket, promo_valid_until: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 mt-1 text-sm"
                          data-testid="admin-manage-promo-valid-until"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Máx. canjes (0 = ∞)</Label>
                        <Input
                          type="number"
                          min={0}
                          value={newTicket.promo_max_redemptions ?? 0}
                          onChange={(e) => setNewTicket({ ...newTicket, promo_max_redemptions: parseInt(e.target.value) || 0 })}
                          className="bg-zinc-800 border-zinc-700 mt-1"
                          data-testid="admin-manage-promo-max-redemptions"
                        />
                      </div>
                    </div>
                    {editingTicket?.promo_redemptions_count > 0 && (
                      <p className="text-[10px] text-zinc-500">
                        Canjeada {editingTicket.promo_redemptions_count} vez/veces hasta ahora.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowTicketModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveTicket} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black">
                  {editingTicket ? 'Actualizar' : 'Crear'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Artist Modal */}
          <Dialog open={showArtistModal} onOpenChange={setShowArtistModal}>
            <DialogContent className="bg-zinc-950 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingArtist ? 'Editar Artista' : 'Agregar Artista'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-zinc-400">Nombre del Artista *</Label>
                  <Input
                    value={newArtist.name}
                    onChange={(e) => setNewArtist({ ...newArtist, name: e.target.value })}
                    placeholder="Nombre artístico"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Horario</Label>
                    <Input
                      value={newArtist.schedule}
                      onChange={(e) => setNewArtist({ ...newArtist, schedule: e.target.value })}
                      placeholder="Ej: 02:00 - 03:30"
                      className="bg-zinc-800 border-zinc-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Escenario</Label>
                    <Input
                      value={newArtist.stage}
                      onChange={(e) => setNewArtist({ ...newArtist, stage: e.target.value })}
                      placeholder="Ej: Main Stage"
                      className="bg-zinc-800 border-zinc-700 mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-400">Descripción</Label>
                  <Textarea
                    value={newArtist.description}
                    onChange={(e) => setNewArtist({ ...newArtist, description: e.target.value })}
                    placeholder="Breve descripción del artista"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                    rows={2}
                  />
                </div>

                <div>
                  <Label className="text-zinc-400 flex items-center gap-2">
                    Foto del Artista
                    <span className="text-xs text-zinc-600 font-normal">(Recomendado)</span>
                  </Label>
                  
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 mt-2 mb-3">
                    <div className="flex items-start gap-2">
                      <Info size={14} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-zinc-400 space-y-1">
                        <p className="font-medium text-zinc-300">Recomendaciones:</p>
                        <ul className="list-disc list-inside space-y-0.5 text-zinc-500">
                          <li>Formato cuadrado (1:1) - 500x500 px mínimo</li>
                          <li>JPG o PNG de alta calidad, máximo 5MB</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {newArtist.image_url && (
                    <div className="mb-3 relative inline-block">
                      <img 
                        src={newArtist.image_url} 
                        alt="Preview" 
                        className="w-24 h-24 object-cover rounded-lg border border-zinc-700"
                      />
                      <button
                        type="button"
                        onClick={() => setNewArtist({ ...newArtist, image_url: '' })}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 text-xs"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  <CloudinaryUploader
                    onUploadComplete={(result) => setNewArtist({ ...newArtist, image_url: result.url })}
                    folder="bravo/artists"
                    buttonText="Subir foto"
                    showPreview={false}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowArtistModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveArtist} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black">
                  {editingArtist ? 'Actualizar' : 'Agregar'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Cancel Event Modal */}
          <AnimatePresence>
            {showCancelModal && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-zinc-900 border border-red-500/30 rounded-xl max-w-md w-full p-6"
                >
                  <div className="text-center mb-6">
                    <AlertTriangle className="mx-auto mb-4 text-red-500" size={48} />
                    <h3 className="text-xl font-bold text-white mb-2">¿Cancelar Evento?</h3>
                    <p className="text-zinc-400 text-sm">
                      Esta acción <strong className="text-red-400">NO tiene vuelta atrás</strong>. 
                      El evento quedará bloqueado, no aparecerá en la página pública y no se podrá vender más entradas.
                    </p>
                  </div>

                  <div className="mb-4">
                    <Label className="text-zinc-400 text-sm">
                      Escribe <strong className="text-red-400">CANCELAR EVENTO</strong> para confirmar
                    </Label>
                    <Input
                      value={cancelConfirmText}
                      onChange={(e) => setCancelConfirmText(e.target.value)}
                      placeholder="CANCELAR EVENTO"
                      className="bg-zinc-800 border-zinc-700 mt-2 text-center font-mono"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowCancelModal(false);
                        setCancelConfirmText('');
                      }}
                      className="flex-1 border-zinc-700"
                    >
                      No, volver
                    </Button>
                    <Button
                      onClick={handleCancelEvent}
                      disabled={saving || cancelConfirmText !== 'CANCELAR EVENTO'}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <XCircle size={16} className="mr-2" />}
                      Cancelar Evento
                    </Button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Edit Order Modal */}
          <Dialog open={showEditOrderModal} onOpenChange={setShowEditOrderModal}>
            <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">Editar Orden</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Buyer Information Section */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Nombre del Comprador</Label>
                    <Input value={editOrderForm.buyer_name} onChange={(e) => setEditOrderForm({ ...editOrderForm, buyer_name: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Email</Label>
                    <Input type="email" value={editOrderForm.buyer_email} onChange={(e) => setEditOrderForm({ ...editOrderForm, buyer_email: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Teléfono</Label>
                    <Input value={editOrderForm.buyer_phone} onChange={(e) => setEditOrderForm({ ...editOrderForm, buyer_phone: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-400">DNI Comprador</Label>
                    <Input value={editOrderForm.buyer_dni} onChange={(e) => setEditOrderForm({ ...editOrderForm, buyer_dni: e.target.value })} placeholder="Ej: 12345678" className="bg-zinc-800 border-zinc-700 mt-1" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Estado</Label>
                    <Select value={editOrderForm.status} onValueChange={(v) => setEditOrderForm({ ...editOrderForm, status: v })}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800">
                        <SelectItem value="completed">Completado</SelectItem>
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                        <SelectItem value="refunded">Reembolsado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Notas Admin</Label>
                    <Textarea value={editOrderForm.admin_notes} onChange={(e) => setEditOrderForm({ ...editOrderForm, admin_notes: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" rows={2} />
                  </div>
                </div>

                {/* Individual Tickets Section */}
                {selectedOrder && selectedOrder.quantity > 0 && (
                  <div className="border-t border-zinc-800 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-[#D4AF37] font-bold flex items-center gap-2">
                        <Ticket size={16} />
                        Entradas Individuales ({selectedOrder.quantity})
                      </Label>
                      {orderTicketsLoading && <Loader2 className="animate-spin text-zinc-500" size={16} />}
                    </div>
                    <p className="text-xs text-zinc-500 mb-3">Editar el DNI de cada entrada individualmente</p>
                    
                    {orderTickets.length > 0 ? (
                      <div className="space-y-2">
                        {orderTickets.map((ticket, index) => (
                          <div 
                            key={ticket.id} 
                            className={`p-3 rounded-lg border ${
                              ticket.checked_in 
                                ? 'bg-green-500/10 border-green-500/30' 
                                : 'bg-zinc-800/50 border-zinc-700'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                  ticket.checked_in ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'
                                }`}>
                                  {index + 1}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-white flex items-center gap-2">
                                    <span className="font-mono text-xs text-zinc-500">{ticket.ticket_code}</span>
                                    {ticket.tier_name && <span className="text-zinc-400">• {ticket.tier_name}</span>}
                                    {ticket.checked_in && <CheckCircle size={14} className="text-green-400" />}
                                  </p>
                                  
                                  {/* DNI Display/Edit */}
                                  {editingTicketId === ticket.id ? (
                                    <div className="flex items-center gap-2 mt-1">
                                      <Input
                                        value={editingTicketDni}
                                        onChange={(e) => setEditingTicketDni(e.target.value.replace(/\D/g, ''))}
                                        placeholder="DNI del titular"
                                        className="h-7 w-32 bg-zinc-700 border-zinc-600 text-sm"
                                        maxLength={8}
                                      />
                                      <Button 
                                        size="sm" 
                                        className="h-7 px-2 bg-green-600 hover:bg-green-700"
                                        onClick={() => handleUpdateTicketDni(ticket.id)}
                                        disabled={actionLoading}
                                      >
                                        {actionLoading ? <Loader2 className="animate-spin" size={12} /> : <CheckCircle size={12} />}
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        className="h-7 px-2"
                                        onClick={() => { setEditingTicketId(null); setEditingTicketDni(''); }}
                                      >
                                        <XCircle size={12} />
                                      </Button>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-zinc-500 mt-0.5">
                                      DNI Titular: <span className="font-mono text-zinc-300">{ticket.holder_dni || 'No asignado'}</span>
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Edit DNI Button */}
                              {editingTicketId !== ticket.id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-zinc-400 hover:text-white"
                                  onClick={() => {
                                    setEditingTicketId(ticket.id);
                                    setEditingTicketDni(ticket.holder_dni || '');
                                  }}
                                >
                                  <Edit2 size={12} className="mr-1" />
                                  Editar
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : !orderTicketsLoading ? (
                      <p className="text-zinc-500 text-sm text-center py-4 bg-zinc-800/30 rounded-lg">
                        No se encontraron tickets individuales para esta orden
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowEditOrderModal(false)}>Cancelar</Button>
                <Button onClick={handleSaveEditOrder} disabled={actionLoading} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black">
                  {actionLoading ? <Loader2 className="animate-spin mr-2" size={14} /> : null}Guardar Datos Comprador
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Refund Modal */}
          <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
            <DialogContent className="bg-zinc-950 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white">Reembolsar Orden</DialogTitle>
              </DialogHeader>
              {selectedOrder && (
                <div className="space-y-4 py-4">
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <p className="text-sm text-zinc-500">Monto Total</p>
                    <p className="text-2xl font-black">${selectedOrder.total_amount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Monto a Reembolsar (vacío = total)</Label>
                    <Input type="number" value={refundForm.partial_amount} onChange={(e) => setRefundForm({ ...refundForm, partial_amount: e.target.value })} placeholder={`Máx: $${selectedOrder.total_amount}`} className="bg-zinc-800 border-zinc-700 mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Motivo</Label>
                    <Textarea value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" rows={2} />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowRefundModal(false)}>Cancelar</Button>
                {refundForm.partial_amount ? (
                  <Button onClick={() => handleRefund(true)} disabled={actionLoading} className="bg-purple-600 hover:bg-purple-700 text-white">
                    {actionLoading ? <Loader2 className="animate-spin mr-2" size={14} /> : null}Reembolso Parcial
                  </Button>
                ) : (
                  <Button onClick={() => handleRefund(false)} disabled={actionLoading} className="bg-red-600 hover:bg-red-700 text-white">
                    {actionLoading ? <Loader2 className="animate-spin mr-2" size={14} /> : null}Reembolso Total
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Order Details Modal */}
          <Dialog open={showOrderDetailsModal} onOpenChange={setShowOrderDetailsModal}>
            <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">Detalles de la Orden</DialogTitle>
              </DialogHeader>
              {selectedOrder && (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-zinc-800/50 rounded-lg"><p className="text-xs text-zinc-500">ID</p><code className="text-sm">{selectedOrder.id}</code></div>
                    <div className="p-3 bg-zinc-800/50 rounded-lg"><p className="text-xs text-zinc-500">Estado</p>{getStatusBadge(selectedOrder.status)}</div>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <p className="text-xs text-zinc-500 mb-2">Comprador Principal</p>
                    <p className="font-medium">{selectedOrder.buyer_name || '-'}</p>
                    <p className="text-sm text-zinc-400">{selectedOrder.buyer_email}</p>
                    <p className="text-sm text-zinc-400">{selectedOrder.buyer_phone || '-'}</p>
                    {selectedOrder.buyer_dni && (
                      <p className="text-sm text-zinc-400 mt-1">DNI: <span className="font-mono">{selectedOrder.buyer_dni}</span></p>
                    )}
                  </div>

                  {/* Price breakdown with discount */}
                  {(() => {
                    const qty = selectedOrder.quantity || 1;
                    const unitPrice = selectedOrder.unit_price || 0;
                    const subtotal = selectedOrder.subtotal || (unitPrice * qty);
                    const discountAmount = selectedOrder.discount_amount || 0;
                    const discountCode = selectedOrder.discount?.code || selectedOrder.discount_code || null;
                    const totalAmount = selectedOrder.total_amount || 0;
                    const serviceFee = Math.max(0, totalAmount - subtotal + discountAmount);
                    const netSubtotal = Math.max(0, subtotal - discountAmount);
                    return (
                      <div className="p-3 bg-zinc-800/50 rounded-lg space-y-2" data-testid="admin-order-details-breakdown">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Desglose de precio</p>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-zinc-400">Subtotal ({qty} × ${unitPrice.toLocaleString()})</span>
                          <span className="font-mono text-white">${subtotal.toLocaleString()}</span>
                        </div>
                        {discountAmount > 0 && (
                          <>
                            <div className="flex items-center justify-between text-sm" data-testid="admin-order-details-discount-row">
                              <span className="text-amber-400 flex items-center gap-1.5">
                                <Tag size={12} />
                                Descuento
                                {discountCode && (
                                  <span className="font-mono bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded text-[10px]">
                                    {discountCode}
                                  </span>
                                )}
                              </span>
                              <span className="font-mono text-amber-400">−${discountAmount.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm pt-1 border-t border-zinc-700/50">
                              <span className="text-zinc-300">Subtotal con descuento</span>
                              <span className="font-mono font-bold text-emerald-400">${netSubtotal.toLocaleString()}</span>
                            </div>
                          </>
                        )}
                        {serviceFee > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-500">Cargo de servicio</span>
                            <span className="font-mono text-zinc-400">${serviceFee.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-sm pt-2 border-t border-zinc-700">
                          <span className="text-zinc-300 font-semibold">Total pagado</span>
                          <span className="font-mono font-bold text-white">${totalAmount.toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Individual Tickets Section */}
                  <div className="border-t border-zinc-800 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold uppercase text-[#D4AF37] flex items-center gap-2">
                        <Ticket size={16} />
                        Entradas Individuales ({selectedOrder.quantity})
                      </h4>
                      {orderTicketsLoading && <Loader2 className="animate-spin text-zinc-500" size={16} />}
                    </div>
                    
                    {orderTickets.length > 0 ? (
                      <div className="space-y-2">
                        {orderTickets.map((ticket, index) => (
                          <div 
                            key={ticket.id} 
                            className={`p-3 rounded-lg border ${ticket.checked_in ? 'bg-green-500/10 border-green-500/30' : 'bg-zinc-800/50 border-zinc-700'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${ticket.checked_in ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                                  {index + 1}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-white flex items-center gap-2">
                                    <span className="font-mono text-xs text-zinc-500">{ticket.ticket_code}</span>
                                    {ticket.tier_name && <span className="text-zinc-400">• {ticket.tier_name}</span>}
                                    {ticket.checked_in && <CheckCircle size={14} className="text-green-400" />}
                                  </p>
                                  
                                  {/* DNI Display/Edit */}
                                  {editingTicketId === ticket.id ? (
                                    <div className="flex items-center gap-2 mt-1">
                                      <Input
                                        value={editingTicketDni}
                                        onChange={(e) => setEditingTicketDni(e.target.value)}
                                        placeholder="DNI del titular"
                                        className="h-7 w-32 bg-zinc-700 border-zinc-600 text-sm"
                                        maxLength={8}
                                      />
                                      <Button 
                                        size="sm" 
                                        className="h-7 px-2 bg-green-600 hover:bg-green-700"
                                        onClick={() => handleUpdateTicketDni(ticket.id)}
                                        disabled={actionLoading}
                                      >
                                        {actionLoading ? <Loader2 className="animate-spin" size={12} /> : <CheckCircle size={12} />}
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        className="h-7 px-2"
                                        onClick={() => { setEditingTicketId(null); setEditingTicketDni(''); }}
                                      >
                                        <XCircle size={12} />
                                      </Button>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-zinc-500 mt-0.5">
                                      DNI Titular: <span className="font-mono text-zinc-300">{ticket.holder_dni || 'No asignado'}</span>
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Edit DNI Button */}
                              {editingTicketId !== ticket.id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-zinc-400 hover:text-white"
                                  onClick={() => {
                                    setEditingTicketId(ticket.id);
                                    setEditingTicketDni(ticket.holder_dni || '');
                                  }}
                                >
                                  <Edit2 size={12} className="mr-1" />
                                  Editar DNI
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : !orderTicketsLoading ? (
                      <p className="text-zinc-500 text-sm text-center py-4">No se encontraron tickets para esta orden</p>
                    ) : null}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Report Modal */}
          <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
            <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">Reporte Completo: {event?.title}</DialogTitle>
              </DialogHeader>
              {report && (
                <div className="space-y-6 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-zinc-800/50 rounded-lg"><p className="text-xs text-zinc-500">Tickets Vendidos</p><p className="text-2xl font-black">{report.summary?.total_tickets_sold}</p></div>
                    <div className="p-3 bg-zinc-800/50 rounded-lg"><p className="text-xs text-zinc-500">Asistentes</p><p className="text-2xl font-black">{report.summary?.checked_in}</p></div>
                    <div className="p-3 bg-zinc-800/50 rounded-lg"><p className="text-xs text-zinc-500">Ingreso Bruto</p><p className="text-2xl font-black">${report.summary?.total_revenue?.toLocaleString()}</p></div>
                    <div className="p-3 bg-zinc-800/50 rounded-lg"><p className="text-xs text-zinc-500">Ingreso Neto</p><p className="text-2xl font-black text-green-400">${report.summary?.net_revenue?.toLocaleString()}</p></div>
                    {(report.summary?.discount_total || 0) > 0 && (
                      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg col-span-2">
                        <p className="text-xs text-amber-400 uppercase tracking-wide">Descuentos aplicados (códigos)</p>
                        <p className="text-2xl font-black text-amber-400" data-testid="admin-manage-discount-total">−${report.summary?.discount_total?.toLocaleString()}</p>
                        <p className="text-xs text-amber-500/70 mt-1">Ya descontado del Ingreso Neto.</p>
                      </div>
                    )}
                  </div>
                  {report.tickets_by_tier && Object.keys(report.tickets_by_tier).length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold uppercase text-[#D4AF37] mb-3">Por Tipo de Entrada</h4>
                      <div className="space-y-2">
                        {Object.entries(report.tickets_by_tier).map(([tier, count]) => (
                          <div key={tier} className="flex justify-between items-center p-3 bg-zinc-800/50 rounded-lg">
                            <span>{tier}</span><span className="font-mono font-bold">{count} tickets</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* RRPP Modal */}
          <Dialog open={showRRPPModal} onOpenChange={setShowRRPPModal}>
            <DialogContent className="bg-zinc-950 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white">Agregar RRPP</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-zinc-400">Nombre *</Label>
                  <Input value={newRRPP.name} onChange={(e) => setNewRRPP({ ...newRRPP, name: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
                </div>
                <div>
                  <Label className="text-zinc-400">Email (opcional)</Label>
                  <Input type="email" value={newRRPP.email} onChange={(e) => setNewRRPP({ ...newRRPP, email: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
                </div>
                <div>
                  <Label className="text-zinc-400">Teléfono (opcional)</Label>
                  <Input value={newRRPP.phone} onChange={(e) => setNewRRPP({ ...newRRPP, phone: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
                </div>
                <div>
                  <Label className="text-zinc-400">Comisión %</Label>
                  <Input type="number" value={newRRPP.commission_percent} onChange={(e) => setNewRRPP({ ...newRRPP, commission_percent: parseFloat(e.target.value) || 10 })} min="0" max="50" className="bg-zinc-800 border-zinc-700 mt-1 w-24" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowRRPPModal(false)}>Cancelar</Button>
                <Button onClick={handleCreateRRPP} disabled={actionLoading} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black">
                  {actionLoading ? <Loader2 className="animate-spin mr-2" size={14} /> : <Plus size={14} className="mr-2" />}Agregar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Invite Modal */}
          <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
            <DialogContent className="bg-zinc-950 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white">Enviar Invitaciones</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-zinc-400">Emails (uno por línea o separados por coma)</Label>
                  <Textarea value={inviteForm.emails} onChange={(e) => setInviteForm({ ...inviteForm, emails: e.target.value })} placeholder="email1@ejemplo.com&#10;email2@ejemplo.com" className="bg-zinc-800 border-zinc-700 mt-1" rows={4} />
                </div>
                <div>
                  <Label className="text-zinc-400">Tipo de Entrada (opcional)</Label>
                  <Select value={inviteForm.tier_id} onValueChange={(v) => setInviteForm({ ...inviteForm, tier_id: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1"><SelectValue placeholder="Cualquier entrada" /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="">Cualquier entrada</SelectItem>
                      {tickets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400">Cantidad por Invitación</Label>
                  <Input type="number" value={inviteForm.quantity_per_invite} onChange={(e) => setInviteForm({ ...inviteForm, quantity_per_invite: parseInt(e.target.value) || 1 })} min="1" className="bg-zinc-800 border-zinc-700 mt-1 w-24" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowInviteModal(false)}>Cancelar</Button>
                <Button onClick={handleSendInvitations} disabled={actionLoading} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black">
                  {actionLoading ? <Loader2 className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}Enviar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default AdminEventManagePage;
